import React, { useState } from "react";
import { Link } from "react-router-dom";

import Seo from "../seo/Seo";
import {
  organizationSchema,
  webPageSchema,
  breadcrumbSchema,
} from "../seo/schema";

import PageHeader from "../components/PageHeader";
import Breadcrumbs from "../components/Breadcrumbs";
import MonthlyChartTable from "../components/MonthlyChartTable";
import MonthYearFilter from "../components/MonthYearFilter";
import AppPromo from "../components/AppPromo";

import { GAMES_BY_TIME } from "../data/games";
import { useNowIST, useChartResults } from "../lib/useResults";
import { formatMonthYear } from "../lib/results";

const PATH = "/satta-chart";

const Chart = () => {
  const nowIST = useNowIST();
  const currentYear = nowIST.getFullYear();
  const currentMonthIndex = nowIST.getMonth();

  const [selectedMonthIndex, setSelectedMonthIndex] =
    useState(currentMonthIndex);
  const [selectedYear, setSelectedYear] = useState(currentYear);

  const { chartResults, isLoading, error } = useChartResults(selectedYear);

  const monthLabel = formatMonthYear(selectedMonthIndex, selectedYear);
  const title = `Satta Chart ${monthLabel} — All Games Record Chart | Bagad Satta Company`;
  const description = `Complete satta chart for ${monthLabel}. Day-by-day record of Disawar, Bagad, Delhi Bazar, Shri Ganesh, Faridabad, Jai Bhawani, Ghaziabad, Jai Hanuman, Gogamedi and Gali results, from 2024 to ${currentYear}.`;

  const trail = [
    { name: "Home", path: "/" },
    { name: "Satta Chart", path: PATH },
  ];

  return (
    <>
      <Seo
        path={PATH}
        title={title}
        description={description}
        keywords="satta chart, satta king chart, monthly satta record chart, disawar chart, gali chart, faridabad chart, ghaziabad chart, bagad chart 2026"
        modifiedTime={nowIST.toISOString()}
        schema={[
          organizationSchema(),
          webPageSchema({
            path: PATH,
            title,
            description,
            modifiedTime: nowIST.toISOString(),
          }),
          breadcrumbSchema(trail),
        ]}
      />

      <div className="page-banner">
        <div className="container page-padding">
          <Breadcrumbs trail={trail} />

          <PageHeader
            h1="Satta Chart — Monthly Record of All Games"
            intro={`Every declared number for all ten games, one month at a time. Records run from January 2024 through ${monthLabel}.`}
            nowIST={nowIST}
          />

          <section className="card monthly-card" aria-labelledby="chart-title">
            <h2 className="card-title card-h3" id="chart-title">
              Satta Record Chart — {monthLabel}
            </h2>

            {error && (
              <div className="error-box small" style={{ marginBottom: 8 }}>
                {error}
              </div>
            )}

            <MonthYearFilter
              selectedYear={selectedYear}
              selectedMonthIndex={selectedMonthIndex}
              currentYear={currentYear}
              currentMonthIndex={currentMonthIndex}
              onYearChange={(yr) => {
                setSelectedYear(yr);
                if (yr === currentYear && selectedMonthIndex > currentMonthIndex) {
                  setSelectedMonthIndex(currentMonthIndex);
                }
              }}
              onMonthChange={setSelectedMonthIndex}
              idPrefix="chart"
            />

            <MonthlyChartTable
              games={GAMES_BY_TIME}
              chartResults={chartResults}
              selectedMonthIndex={selectedMonthIndex}
              selectedYear={selectedYear}
              isLoading={isLoading}
            />
          </section>

          <AppPromo />

          <section className="card text-content">
            <h2>How to Read the Satta Chart</h2>
            <p>
              Each row is a date in {monthLabel} and each column is one game.
              The first column stays fixed as you scroll sideways, so you never
              lose track of which date you are reading. A cell showing{" "}
              <strong>XX</strong> means no number was declared for that game on
              that date — most often because the result time has not yet passed.
            </p>

            <h2>Single-Game Charts</h2>
            <p>
              For one game&apos;s history on its own — with its result time,
              today&apos;s number and its own FAQ — open its dedicated page:
            </p>
            <ul className="related-links">
              {GAMES_BY_TIME.map((game) => (
                <li key={game.key}>
                  <Link to={`/${game.slug}`}>
                    {game.name} Satta Chart — {game.timeLabel}
                  </Link>
                </li>
              ))}
            </ul>

            <h2>Chart Archive</h2>
            <p>
              Records are kept for 2024, 2025 and {currentYear}. Use the year
              selector above to switch between them; the month list adjusts so
              you can never land on a month that has not happened yet.
            </p>
          </section>
        </div>
      </div>
    </>
  );
};

export default Chart;
